import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { releaseApi, type PaginatedReleasesResponse } from "@/lib/api";
import type { Release, ReleaseType } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import type { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";

interface UseReleasesOptions {
  enabled?: boolean;
  artistIds?: string[];
  types?: ReleaseType[];
  search?: string;
  title?: string;
  dateRange?: DateRange;
  pageSize?: number;
  ordering?: string;
  page?: number;
}

interface UseReleasesReturn {
  releases: Release[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createReleasesQueryKey = (options: {
  labelId: string | undefined;
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  artistIds?: string[];
  types?: ReleaseType[];
  title?: string;
  dateFrom?: Date;
  dateTo?: Date;
}) => {
  const key: (string | number)[] = ["releases"];

  // Always include labelId first
  if (options.labelId) {
    key.push("label", options.labelId);
  }

  // Add pagination params (most common filters first for better cache hits)
  if (options.page && options.page > 1) {
    key.push("page", options.page);
  }

  if (options.pageSize && options.pageSize !== DEFAULT_PAGE_SIZE) {
    key.push("pageSize", options.pageSize);
  }

  if (options.ordering) {
    key.push("ordering", options.ordering);
  }

  // Add search filters
  const normalizedSearch = options.search?.trim() || null;
  if (normalizedSearch) {
    key.push("search", normalizedSearch);
  }

  const normalizedTitle = options.title?.trim() || null;
  if (normalizedTitle) {
    key.push("title", normalizedTitle);
  }

  // Normalize and sort arrays for consistent caching
  if (options.artistIds?.length) {
    const sortedArtistIds = [...options.artistIds].sort();
    key.push("artistIds", sortedArtistIds.join(","));
  }

  if (options.types?.length) {
    const sortedTypes = [...options.types].sort();
    key.push("types", sortedTypes.join(","));
  }

  // Add date range
  if (options.dateFrom) {
    key.push("dateFrom", format(options.dateFrom, "yyyy-MM-dd"));
  }

  if (options.dateTo) {
    key.push("dateTo", format(options.dateTo, "yyyy-MM-dd"));
  }

  return key;
};

export const useReleases = ({
  enabled = true,
  artistIds = [],
  types = [],
  search,
  title,
  dateRange,
  pageSize,
  ordering,
  page,
}: UseReleasesOptions = {}): UseReleasesReturn => {
  const { currentLabel } = useOrganizationStore();

  const currentPage = page ?? 1;

  const buildQueryKey = (pageNum: number) =>
    createReleasesQueryKey({
      labelId: currentLabel?.id,
      page: pageNum,
      pageSize,
      ordering,
      search,
      artistIds,
      types,
      title,
      dateFrom: dateRange?.from,
      dateTo: dateRange?.to,
    });

  const buildQueryFn = (pageNum: number) => async (): Promise<PaginatedReleasesResponse> => {
    return await releaseApi.getReleases({
      label_id: currentLabel?.id,
      page: pageNum,
      page_size: pageSize,
      ordering,
      q: search || undefined,
      contributor_ids: artistIds.length > 0 ? artistIds.join(",") : undefined,
      type: types.length > 0 ? types.join(",") : undefined,
      title: title || undefined,
      release_date_after: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined,
      release_date_before: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined,
    });
  };

  const { data, isLoading, isError, error, isSuccess, refetch, isPlaceholderData } = useQuery({
    queryKey: buildQueryKey(currentPage),
    queryFn: buildQueryFn(currentPage),
    enabled,
    placeholderData: keepPreviousData,
  });

  // Prefetch next page in parallel (only when current data is fresh, not placeholder)
  useQuery({
    queryKey: buildQueryKey(currentPage + 1),
    queryFn: buildQueryFn(currentPage + 1),
    enabled: enabled && !!data?.next && !isPlaceholderData,
    staleTime: Infinity,
  });

  return {
    releases: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
